import React, { useEffect, useId, useState } from "react";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import { useNavigate } from 'react-router-dom';
import TabPanels from "../../../utils/TabPanel";
import NavBar from "../NavBar/NavBar";
import FooterData from "../NavBar/FooterData";
import BackButton from "../../../utils/BackButton";
import { getIdMeBaseApiUrl } from "../../../utils/UrlConstants";
import { checkUserAuthentication, handleUnauthenticatedUser } from "../../../utils/authUtils";
import InputText from "../../common/InputText";
import Button from "../../common/Button";
import Dropdown from "../../../utils/components/Dropdown";
import { toast } from "react-toastify";
import { displayPhoneNumber, formatPhoneNumber } from "../../../utils/phoneUtils";
import { useIdentityAppService } from "../../../services/IdentityServices";

const MyAccountPage: React.FunctionComponent = () => {
    const navigate = useNavigate();
    const [tabIndex, setTabIndex] = useState(0);
    const [accountInfo, setAccountInfo] = useState<any>(null);
    const [requestHistory, setRequestHistory] = useState<any[]>([]); // For storing request history
    const [isEditMode, setIsEditMode] = useState(false);
    const [editedInfo, setEditedInfo] = useState<any>({
        username: '',
        firstName: '',
        lastName: '',
        jobTitle: '',
        email: '',
        mobile: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zip: '',
        country: ''
    });
    const [errors, setErrors] = useState<any>({});
    const [stateOptions, setStateOptions] = useState<{ value: string; label: string }[]>([]);
    const [countryOptions, setCountryOptions] = useState<{ value: string; label: string }[]>([]);

    // Address suggestion state
    const [suggestedAddress, setSuggestedAddress] = useState<{
        addressLine1: string;
        addressLine2: string;
        city: string;
        state: string;
        zip: string;
        country: string;
    }>({
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zip: '',
        country: ''
    });

    useEffect(() => {
        if (!checkUserAuthentication()) {
            handleUnauthenticatedUser();
            return;
        }

        const token = sessionStorage.getItem("external_token");
        if (token) {
            const parsedToken = JSON.parse(token);
            setAccountInfo(parsedToken); // Set account information from token

            // Parse the full name into first and last names
            const nameParts = parsedToken.name ? parsedToken.name.split(' ') : [];
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';

            setEditedInfo({
                username: parsedToken.username || '',
                firstName: firstName,
                lastName: lastName,
                jobTitle: parsedToken.jobTitle || '',
                email: parsedToken.email || '',
                mobile: parsedToken.mobile || '',
                addressLine1: parsedToken.addressLine1 || '',
                addressLine2: parsedToken.addressLine2 || '',
                city: parsedToken.city || '',
                state: parsedToken.state || '',
                zip: parsedToken.zip || '',
                country: parsedToken.country || ''
            }); // Initialize edited info with parsed data

            // Fetch request history for the username from token
            const username = parsedToken.username; // Extract username from the token
            if (username) {
                useIdentityAppService().getRequestHistory(username)
                    .then(response => {
                        setRequestHistory(response.data); // Set the request history data
                    })
                    .catch(error => {
                        console.error("Error fetching request history:", error);
                    });

                // Fetch user details to get address and organization information
                useIdentityAppService().getUserDetails(username)
                    .then(response => {
                        if (response.data && response.data.success) {
                            const userData = response.data.user;
                            setEditedInfo((prev: any) => ({
                                ...prev,
                                jobTitle: userData.title || '',
                                addressLine1: userData.addressLine1 || '',
                                addressLine2: userData.addressLine2 || '',
                                city: userData.city || '',
                                state: userData.state || '',
                                zip: userData.zip || '',
                                country: userData.country || ''
                            }));
                            // Update account info with address and organization data
                            setAccountInfo((prev: any) => ({
                                ...prev,
                                jobTitle: userData.title || '',
                                addressLine1: userData.addressLine1 || '',
                                addressLine2: userData.addressLine2 || '',
                                city: userData.city || '',
                                state: userData.state || '',
                                zip: userData.zip || '',
                                country: userData.country || '',
                                organizationName: userData.organizationName || '',
                                organizationType: userData.organizationType || '',
                                stateIssuedId: userData.stateIssuedId || ''
                            }));
                        }
                    })
                    .catch(error => {
                        console.error("Error fetching user details:", error);
                    });
            }
        }
    }, []);

    // Effect to fetch state codes
    useEffect(() => {
        const fetchStateCodes = async () => {
            try {
                const response = await useIdentityAppService().getCodeLookupByType();

                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.codes) {
                        const states = data.codes.map((code: any) => ({
                            value: code.code,
                            label: code.displayLabel || `${code.code} - ${code.value}`
                        }));
                        setStateOptions(states);
                    }
                }
            } catch (error) {
                console.error('Error fetching state codes:', error);
            }
        };

        fetchStateCodes();
    }, []);

    // Effect to fetch country codes
    useEffect(() => {
        const fetchCountryCodes = async () => {
            try {
                const response = await useIdentityAppService().getCodeLookupByTypeCountryDropdown();

                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.codes) {
                        const countries = data.codes.map((code: any) => ({
                            value: code.code,
                            label: code.displayLabel || `${code.code} - ${code.value}`
                        }));
                        setCountryOptions(countries);

                        // Default to USA if country is not already set
                        setEditedInfo((prev: any) => {
                            if (!prev.country || prev.country.trim() === '') {
                                const usaCountry = countries.find((c: any) => c.value === 'US' || c.value === 'USA');
                                return {
                                    ...prev,
                                    country: usaCountry ? usaCountry.value : ''
                                };
                            }
                            return prev;
                        });
                    }
                }
            } catch (error) {
                console.error('Error fetching country codes:', error);
            }
        };

        fetchCountryCodes();
    }, []);

    const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setTabIndex(newValue);
    };

    // Handle back button click - navigate to external home
    const handleBackButtonClick = (event: React.MouseEvent) => {
        event.preventDefault();
        navigate('/external-home');
    };

    const handleEdit = () => {
        setIsEditMode(true);
        // Parse the full name into first and last names
        const nameParts = accountInfo.name ? accountInfo.name.split(' ') : [];
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        setEditedInfo({
            username: accountInfo.username || '',
            firstName: firstName,
            lastName: lastName,
            jobTitle: accountInfo.jobTitle || '',
            email: accountInfo.email || '',
            mobile: accountInfo.mobile || '',
            addressLine1: accountInfo.addressLine1 || '',
            addressLine2: accountInfo.addressLine2 || '',
            city: accountInfo.city || '',
            state: accountInfo.state || '',
            zip: accountInfo.zip || '',
            country: accountInfo.country || ''
        });
    };

    const handleCancel = () => {
        setIsEditMode(false);
        // Reset to original values
        const nameParts = accountInfo.name ? accountInfo.name.split(' ') : [];
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        setEditedInfo({
            username: accountInfo.username || '',
            firstName: firstName,
            lastName: lastName,
            jobTitle: accountInfo.jobTitle || '',
            email: accountInfo.email || '',
            mobile: accountInfo.mobile || '',
            addressLine1: accountInfo.addressLine1 || '',
            addressLine2: accountInfo.addressLine2 || '',
            city: accountInfo.city || '',
            state: accountInfo.state || '',
            zip: accountInfo.zip || '',
            country: accountInfo.country || ''
        });
        setErrors({});
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;

        // Special handling for mobile field - format as (XXX)-XXX-XXXX
        if (name === 'mobile') {
            const formattedPhone = formatPhoneNumber(value);
            setEditedInfo((prev: any) => ({
                ...prev,
                mobile: formattedPhone
            }));
        } else {
            setEditedInfo((prev: any) => ({
                ...prev,
                [name]: value
            }));
        }

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prev: any) => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = event.target;
        setEditedInfo((prev: any) => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user selects an option
        if (errors[name]) {
            setErrors((prev: any) => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Handle address suggestion - for now, returns the same address
    // This will be replaced with API call later
    const handleSuggestAddress = () => {
        // For now, just use the current address as the "suggested" address
        // Later, this will call an API to validate/suggest the address
        const currentAddress = {
            addressLine1: editedInfo.addressLine1 || '',
            addressLine2: editedInfo.addressLine2 || '',
            city: editedInfo.city || '',
            state: editedInfo.state || '',
            zip: editedInfo.zip || '',
            country: editedInfo.country || ''
        };

        setSuggestedAddress(currentAddress);

        // Apply the suggested address to the form
        setEditedInfo((prev: any) => ({
            ...prev,
            ...currentAddress
        }));
    };

    const validateForm = () => {
        const newErrors: any = {};

        if (!editedInfo.firstName?.trim()) {
            newErrors.firstName = 'First name is required';
        }
        if (!editedInfo.lastName?.trim()) {
            newErrors.lastName = 'Last name is required';
        }

        if (!editedInfo.email?.trim()) {
            newErrors.email = 'Email is required';
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(editedInfo.email)) {
                newErrors.email = 'Please enter a valid email address';
            }
        }

        if (!editedInfo.mobile?.trim()) {
            newErrors.mobile = 'Phone number is required';
        } else {
            const phoneRegex = /^[\d\s\-\(\)\+]+$/;
            if (!phoneRegex.test(editedInfo.mobile)) {
                newErrors.mobile = 'Please enter a valid phone number';
            }
        }

        // Address validation - if any address field is filled, all required fields must be filled
        const addressFieldsFilled = editedInfo.addressLine1?.trim() || editedInfo.city?.trim() ||
            editedInfo.state?.trim() || editedInfo.zip?.trim() || editedInfo.country?.trim();

        if (addressFieldsFilled) {
            if (!editedInfo.addressLine1?.trim()) {
                newErrors.addressLine1 = 'Address Line 1 is required when providing address';
            }
            if (!editedInfo.city?.trim()) {
                newErrors.city = 'City is required when providing address';
            }
            if (!editedInfo.state?.trim()) {
                newErrors.state = 'State is required when providing address';
            }
            if (!editedInfo.zip?.trim()) {
                newErrors.zip = 'ZIP code is required when providing address';
            }
            if (!editedInfo.country?.trim()) {
                newErrors.country = 'Country is required when providing address';
            }
        }

        // ZIP code format validation
        if (editedInfo.zip?.trim()) {
            const zipRegex = /^\d{5}(-\d{4})?$/;
            if (!zipRegex.test(editedInfo.zip)) {
                newErrors.zip = 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)';
            }
        }

        return newErrors;
    };

    const handleSave = async () => {
        const newErrors = validateForm();

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }


        const normalizePhone = (input : any) => {
            if (!input) return input;
            return input.replace(/\D/g, "");
        };

        try {
            // Base64 encode the email before sending to API
            const encodedEmail = btoa(editedInfo.email);
            const updateData = {
                username: accountInfo.username,
                firstName: editedInfo.firstName,
                lastName: editedInfo.lastName,
                jobTitle: editedInfo.jobTitle,
                email: encodedEmail,
                phone: normalizePhone(editedInfo.mobile),
                addressLine1: editedInfo.addressLine1,
                addressLine2: editedInfo.addressLine2,
                city: editedInfo.city,
                state: editedInfo.state,
                zip: editedInfo.zip,
                country: editedInfo.country
            };

            const response = await useIdentityAppService().updateUser(updateData);

            if (response.status === 200) {
                // Combine first and last name for display
                const fullName = `${editedInfo.firstName} ${editedInfo.lastName}`.trim();

                // Update account info
                const updatedAccountInfo = {
                    ...accountInfo,
                    name: fullName,
                    jobTitle: editedInfo.jobTitle,
                    email: editedInfo.email,
                    mobile: editedInfo.mobile,
                    addressLine1: editedInfo.addressLine1,
                    addressLine2: editedInfo.addressLine2,
                    city: editedInfo.city,
                    state: editedInfo.state,
                    zip: editedInfo.zip,
                    country: editedInfo.country
                };
                setAccountInfo(updatedAccountInfo);

                // Update session storage
                const token = sessionStorage.getItem('external_token');
                if (token) {
                    const parsedToken = JSON.parse(token);
                    parsedToken.name = fullName;
                    parsedToken.jobTitle = editedInfo.jobTitle;
                    parsedToken.email = editedInfo.email;
                    parsedToken.mobile = editedInfo.mobile;
                    parsedToken.addressLine1 = editedInfo.addressLine1;
                    parsedToken.addressLine2 = editedInfo.addressLine2;
                    parsedToken.city = editedInfo.city;
                    parsedToken.state = editedInfo.state;
                    parsedToken.zip = editedInfo.zip;
                    parsedToken.country = editedInfo.country;
                    sessionStorage.setItem('external_token', JSON.stringify(parsedToken));
                }

                setIsEditMode(false);
                toast.success('Account information updated successfully!');
            }
        } catch (error) {
            console.error('Error updating account information:', error);
            toast.error('Failed to update account information. Please try again.');
        }
    };

    return (
        <React.Fragment>
            <NavBar />
            <Box sx={{ width: '100%' }}>
                <Tabs value={tabIndex} onChange={handleTabChange}>
                    <Tab label="My Account" />
                </Tabs>
                <TabPanels value={tabIndex} index={0}>
                    <Box sx={{
                        marginLeft: 2,
                        marginTop: 2,
                        marginBottom: 2
                    }}>
                        <BackButton onClick={handleBackButtonClick} />
                    </Box>
                    {accountInfo && (
                        <Box sx={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
                            <Typography variant="h6" sx={{ marginBottom: '20px', color: '#00607f', fontWeight: 'bold' }}>
                                Account Information
                            </Typography>

                            {!isEditMode ? (
                                // View Mode
                                <Box>
                                    <Box sx={{ marginBottom: '15px' }}>
                                        <Typography sx={{ fontWeight: 'bold', color: '#666', marginBottom: '5px' }}>
                                            Username:
                                        </Typography>
                                        <Typography sx={{ fontSize: '16px' }}>
                                            {accountInfo.username}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ marginBottom: '15px' }}>
                                        <Typography sx={{ fontWeight: 'bold', color: '#666', marginBottom: '5px' }}>
                                            Name:
                                        </Typography>
                                        <Typography sx={{ fontSize: '16px' }}>
                                            {accountInfo.name}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ marginBottom: '15px' }}>
                                        <Typography sx={{ fontWeight: 'bold', color: '#666', marginBottom: '5px' }}>
                                            Email:
                                        </Typography>
                                        <Typography sx={{ fontSize: '16px' }}>
                                            {accountInfo.email}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ marginBottom: '15px' }}>
                                        <Typography sx={{ fontWeight: 'bold', color: '#666', marginBottom: '5px' }}>
                                            Phone:
                                        </Typography>
                                        <Typography sx={{ fontSize: '16px' }}>
                                            {displayPhoneNumber(accountInfo.mobile)}
                                        </Typography>
                                    </Box>

                                    {accountInfo.jobTitle && (
                                        <Box sx={{ marginBottom: '15px' }}>
                                            <Typography sx={{ fontWeight: 'bold', color: '#666', marginBottom: '5px' }}>
                                                Job Title:
                                            </Typography>
                                            <Typography sx={{ fontSize: '16px' }}>
                                                {accountInfo.jobTitle}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Address Section */}
                                    {(accountInfo.addressLine1 || accountInfo.city || accountInfo.state || accountInfo.zip) && (
                                        <>
                                            <Typography variant="h6" sx={{ marginTop: '20px', marginBottom: '15px', color: '#00607f', fontWeight: 'bold' }}>
                                                Address Information
                                            </Typography>

                                            {accountInfo.addressLine1 && (
                                                <Box sx={{ marginBottom: '15px' }}>
                                                    <Typography sx={{ fontWeight: 'bold', color: '#666', marginBottom: '5px' }}>
                                                        Address:
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '16px' }}>
                                                        {accountInfo.addressLine1}
                                                        {accountInfo.addressLine2 && <><br />{accountInfo.addressLine2}</>}
                                                    </Typography>
                                                </Box>
                                            )}

                                            <Box sx={{ display: 'flex', gap: '40px', marginBottom: '20px' }}>
                                                {accountInfo.city && (
                                                    <Box>
                                                        <Typography sx={{ fontWeight: 'bold', color: '#666', marginBottom: '5px' }}>
                                                            City:
                                                        </Typography>
                                                        <Typography sx={{ fontSize: '16px' }}>
                                                            {accountInfo.city}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {accountInfo.state && (
                                                    <Box>
                                                        <Typography sx={{ fontWeight: 'bold', color: '#666', marginBottom: '5px' }}>
                                                            State:
                                                        </Typography>
                                                        <Typography sx={{ fontSize: '16px' }}>
                                                            {stateOptions.find(s => s.value === accountInfo.state)?.label || accountInfo.state}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {accountInfo.zip && (
                                                    <Box>
                                                        <Typography sx={{ fontWeight: 'bold', color: '#666', marginBottom: '5px' }}>
                                                            ZIP:
                                                        </Typography>
                                                        <Typography sx={{ fontSize: '16px' }}>
                                                            {accountInfo.zip}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {accountInfo.country && (
                                                    <Box>
                                                        <Typography sx={{ fontWeight: 'bold', color: '#666', marginBottom: '5px' }}>
                                                            Country:
                                                        </Typography>
                                                        <Typography sx={{ fontSize: '16px' }}>
                                                            {countryOptions.find(c => c.value === accountInfo.country)?.label || accountInfo.country}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </>
                                    )}

                                    {/* Organization Section */}
                                    {(accountInfo.organizationName || accountInfo.organizationType || accountInfo.stateIssuedId) && (
                                        <>
                                            <Typography variant="h6" sx={{ marginTop: '20px', marginBottom: '15px', color: '#00607f', fontWeight: 'bold' }}>
                                                Organization Information
                                            </Typography>

                                            {accountInfo.organizationName && (
                                                <Box sx={{ marginBottom: '15px' }}>
                                                    <Typography sx={{ fontWeight: 'bold', color: '#666', marginBottom: '5px' }}>
                                                        Organization Name:
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '16px' }}>
                                                        {accountInfo.organizationName}
                                                    </Typography>
                                                </Box>
                                            )}

                                            <Box sx={{ display: 'flex', gap: '40px', marginBottom: '20px' }}>
                                                {accountInfo.organizationType && (
                                                    <Box>
                                                        <Typography sx={{ fontWeight: 'bold', color: '#666', marginBottom: '5px' }}>
                                                            Organization Type:
                                                        </Typography>
                                                        <Typography sx={{ fontSize: '16px' }}>
                                                            {accountInfo.organizationType}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {accountInfo.stateIssuedId && (
                                                    <Box>
                                                        <Typography sx={{ fontWeight: 'bold', color: '#666', marginBottom: '5px' }}>
                                                            State Issued ID:
                                                        </Typography>
                                                        <Typography sx={{ fontSize: '16px' }}>
                                                            {accountInfo.stateIssuedId}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </>
                                    )}

                                    <Button onClick={handleEdit}>
                                        Edit Information
                                    </Button>
                                </Box>
                            ) : (
                                // Edit Mode
                                <Box>
                                    <Box sx={{ marginBottom: '20px' }}>
                                        <InputText
                                            label="Username"
                                            name="username"
                                            value={editedInfo.username || ''}
                                            type="text"
                                            onChange={() => { }} // No-op since it's read-only
                                            mandatoryLabel={false}
                                            readOnly={true}
                                            placeholder="Username"
                                        />
                                    </Box>

                                    <Box sx={{ marginBottom: '20px' }}>
                                        <InputText
                                            label="First Name"
                                            name="firstName"
                                            value={editedInfo.firstName || ''}
                                            type="text"
                                            onChange={handleInputChange}
                                            mandatoryLabel={true}
                                            placeholder="Enter your first name"
                                        />
                                        {errors.firstName && (
                                            <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                                {errors.firstName}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box sx={{ marginBottom: '20px' }}>
                                        <InputText
                                            label="Last Name"
                                            name="lastName"
                                            value={editedInfo.lastName || ''}
                                            type="text"
                                            onChange={handleInputChange}
                                            mandatoryLabel={true}
                                            placeholder="Enter your last name"
                                        />
                                        {errors.lastName && (
                                            <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                                {errors.lastName}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box sx={{ marginBottom: '20px' }}>
                                        <InputText
                                            label="Job Title"
                                            name="jobTitle"
                                            value={editedInfo.jobTitle || ''}
                                            type="text"
                                            onChange={handleInputChange}
                                            mandatoryLabel={false}
                                            placeholder="Enter your job title"
                                        />
                                        {errors.jobTitle && (
                                            <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                                {errors.jobTitle}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box sx={{ marginBottom: '20px' }}>
                                        <InputText
                                            label="Email"
                                            name="email"
                                            value={editedInfo.email || ''}
                                            type="email"
                                            onChange={handleInputChange}
                                            mandatoryLabel={true}
                                            placeholder="Enter your email address"
                                        />
                                        {errors.email && (
                                            <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                                {errors.email}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box sx={{ marginBottom: '20px' }}>
                                        <InputText
                                            label="Phone"
                                            name="mobile"
                                            value={editedInfo.mobile || ''}
                                            type="tel"
                                            onChange={handleInputChange}
                                            mandatoryLabel={true}
                                            placeholder="Enter your phone number"
                                        />
                                        {errors.mobile && (
                                            <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                                {errors.mobile}
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Address Section */}
                                    <Typography variant="h6" sx={{ marginTop: '30px', marginBottom: '15px', color: '#00607f', fontWeight: 'bold' }}>
                                        Address Information
                                        <Typography component="span" sx={{ fontSize: '14px', fontWeight: 'normal', color: '#666', marginLeft: '10px' }}>
                                            (All fields except Address Line 2 are required)
                                        </Typography>
                                    </Typography>

                                    <Box sx={{ marginBottom: '20px' }}>
                                        <InputText
                                            label="Address Line 1"
                                            name="addressLine1"
                                            value={editedInfo.addressLine1 || ''}
                                            type="text"
                                            onChange={handleInputChange}
                                            mandatoryLabel={false}
                                            placeholder="Enter street address"
                                        />

                                        {/* Address suggestion helper text and button */}
                                        {editedInfo.addressLine1?.trim() && (
                                            <Box sx={{
                                                marginTop: '5px',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                flexWrap: 'wrap',
                                                gap: '10px'
                                            }}>
                                                <Typography sx={{
                                                    fontSize: '12px',
                                                    color: '#666',
                                                    flex: '1'
                                                }}>
                                                    Click to validate and use suggested address
                                                </Typography>

                                                <button
                                                    type="button"
                                                    onClick={handleSuggestAddress}
                                                    style={{
                                                        fontSize: '12px',
                                                        padding: '4px 8px',
                                                        backgroundColor: '#00607f',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                    onMouseOver={(e) => {
                                                        e.currentTarget.style.backgroundColor = '#004d66';
                                                    }}
                                                    onMouseOut={(e) => {
                                                        e.currentTarget.style.backgroundColor = '#00607f';
                                                    }}
                                                >
                                                    Use Suggested
                                                </button>
                                            </Box>
                                        )}

                                        {errors.addressLine1 && (
                                            <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                                {errors.addressLine1}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box sx={{ marginBottom: '20px' }}>
                                        <InputText
                                            label="Address Line 2"
                                            name="addressLine2"
                                            value={editedInfo.addressLine2 || ''}
                                            type="text"
                                            onChange={handleInputChange}
                                            mandatoryLabel={false}
                                            placeholder="Apartment, suite, unit, etc. (optional)"
                                        />
                                    </Box>

                                    <Box sx={{ marginBottom: '20px' }}>
                                        <InputText
                                            label="City"
                                            name="city"
                                            value={editedInfo.city || ''}
                                            type="text"
                                            onChange={handleInputChange}
                                            mandatoryLabel={false}
                                            placeholder="Enter city"
                                        />
                                        {errors.city && (
                                            <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                                {errors.city}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box sx={{ marginBottom: '20px' }}>
                                        <Dropdown
                                            choices={stateOptions}
                                            emptyChoiceLabel="Select State"
                                            mandatoryLabel={false}
                                            name="state"
                                            label="State"
                                            value={editedInfo.state || ''}
                                            onChange={handleDropdownChange}
                                        />
                                        {errors.state && (
                                            <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                                {errors.state}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box sx={{ marginBottom: '20px' }}>
                                        <InputText
                                            label="ZIP Code"
                                            name="zip"
                                            value={editedInfo.zip || ''}
                                            type="text"
                                            onChange={handleInputChange}
                                            mandatoryLabel={false}
                                            placeholder="12345 or 12345-6789"
                                        />
                                        {errors.zip && (
                                            <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                                {errors.zip}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box sx={{ marginBottom: '30px' }}>
                                        <Dropdown
                                            choices={countryOptions}
                                            emptyChoiceLabel="Select Country"
                                            mandatoryLabel={false}
                                            name="country"
                                            label="Country"
                                            value={editedInfo.country || ''}
                                            onChange={handleDropdownChange}
                                        />
                                        {errors.country && (
                                            <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                                {errors.country}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box sx={{ display: 'flex', gap: '15px' }}>
                                        <Button onClick={handleSave}>
                                            Save
                                        </Button>
                                        <Button onClick={handleCancel}>
                                            Cancel
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </TabPanels>
            </Box>
            <FooterData />
        </React.Fragment>
    );
};

export default MyAccountPage;
